// assets/frontend/src/entry.js
// ───────────────────────────────────────
// Entry point para el frontend público de El Discipulado.
// Interacciones mínimas: filtros, menú móvil, lazy-load de embeds.

import './styles/fonts.scss';
import './styles/main.scss';
import './styles/resource-detail.scss';
import './styles/pages.scss';
import './styles/favorites.scss';
import './styles/auth.scss';
import { createIcons, icons } from 'lucide';

// ── Filtros de catálogo (chips, envío automático, URL) ──
import './js/catalog-filters.js';

// ── Búsqueda: envío desde header, focus, filtros automáticos ──
import './js/search-interactions.js';

// ── Filtrado local en índices de taxonomías (predicadores, series) ──
import './js/taxonomy-filters.js';

// Inicializar íconos Lucide.
document.addEventListener('DOMContentLoaded', () => {
  // lucide >= 0.4xx exige el objeto de íconos explícito (sin él lanza
  // "Please provide an icons object" en consola).
  createIcons({ icons });

  // Tarea 42: accesibilidad/rendimiento de imágenes (WCAG 2.2 + CLS).
  //  1. lazy-load: las imágenes de contenido dinámico (páginas
  //     informativas servidas desde la BD) no traen loading="lazy";
  //     se lo agrega a todas salvo al logo del header (LCP).
  //  2. width/height: las imágenes sin dimensiones declaradas causan
  //     CLS; se declaran con el tamaño natural al cargar (el CSS
  //     `img { max-width: 100%; height: auto }` mantiene la respuesta).
  document.querySelectorAll('img').forEach((img) => {
    if (img.closest('.site-logo')) {
      return; // logo del header: carga prioritaria, sin lazy.
    }
    // Chromium reporta "auto" cuando el atributo no existe (no "eager"
    // ni cadena vacía), por lo que la condición debe ser por defecto:
    // todo <img> que no sea el logo del header se marca como lazy.
    if (img.loading !== 'lazy') {
      img.loading = 'lazy';
    }
    if (!img.hasAttribute('width') || !img.hasAttribute('height')) {
      const declare = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          img.setAttribute('width', String(img.naturalWidth));
          img.setAttribute('height', String(img.naturalHeight));
        }
      };
      if (img.complete) {
        declare();
      } else {
        img.addEventListener('load', declare, { once: true });
      }
    }
  });

  // Tarea 42: flag de listo para tests/auditorías Playwright (el CSS y
  // las mejoras de imágenes están aplicados cuando se setea).
  window.__frontendReady = true;
});

// ── Menú móvil toggle ──
(function initMobileMenu() {
  const toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
  const navMenu = document.querySelector('[data-mobile-nav]');
  if (!toggleBtn || !navMenu) return;

  toggleBtn.addEventListener('click', () => {
    const isOpen = navMenu.classList.toggle('open');
    toggleBtn.setAttribute('aria-expanded', String(isOpen));
  });

  // Cerrar al hacer click fuera
  document.addEventListener('click', (e) => {
    if (navMenu.classList.contains('open') &&
        !navMenu.contains(e.target) &&
        !toggleBtn.contains(e.target)) {
      navMenu.classList.remove('open');
      toggleBtn.setAttribute('aria-expanded', 'false');
    }
  });

  // Cerrar al presionar Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && navMenu.classList.contains('open')) {
      navMenu.classList.remove('open');
      toggleBtn.setAttribute('aria-expanded', 'false');
      toggleBtn.focus();
    }
  });
})();

// ── Lazy load de embeds externos cuando entran en viewport ──
(function initLazyEmbeds() {
  if ('IntersectionObserver' in window) {
    const embedPlaceholders = document.querySelectorAll('[data-lazy-embed]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const placeholder = entry.target;
          const iframeSrc = placeholder.dataset.lazyEmbed;
          const iframe = document.createElement('iframe');
          iframe.src = iframeSrc;
          iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.loading = 'lazy';
          iframe.style.width = '100%';
          iframe.style.aspectRatio = '16/9';
          iframe.style.border = 'none';
          placeholder.replaceWith(iframe);
          observer.unobserve(placeholder);
        }
      });
    }, { rootMargin: '200px' });

    embedPlaceholders.forEach((ph) => observer.observe(ph));
  }
})();
